// ============================================================================
// Facebook Page — capability glue + registration helper.
//
// `buildFacebookPageMethods()` produces the `AdapterMethods<'social-posting'>`
// shape the capability registry expects. The adapter glue:
//   1. Builds a `SocialPostingCallCtx` from the registry's `AdapterContext`
//      (which carries `accessToken`, `instanceId`, `companyId`, optional
//      `config`) plus the runtime ENV (rate-limiter binding, KV namespace).
//   2. Forwards to each per-method helper in `publish.ts`, `upload-media.ts`,
//      `insights.ts`, `list-accounts.ts`.
//   3. The PAT lives ONLY inside this glue layer — every per-method helper
//      receives it as an explicit argument so the redacted logger / tests
//      can verify the value never bleeds into log lines.
//
// Why we don't take a runtime arg directly: the dispatcher builds the
// `AdapterContext` and threads it; the per-call runtime env (CF env binding)
// arrives via dependency injection at `buildFacebookPageMethods(...)` —
// because `register()` happens at module-init when the env isn't available.
// Instead, we accept a `runtime` getter that the adapter calls per method,
// so the Worker request handler binds the env at the latest possible moment.
// ============================================================================

import type { AdapterMethods, AdapterContext } from '../../services/capability-registry'
import type {
  SocialPostingCallCtx,
  SocialPostingRedactedLogger,
} from '../../capabilities/social-posting'
import { register as registerCapability } from '../../services/capability-registry'
import { registerWebhookAdapter } from '../../services/webhook-adapters'
import { createRedactedLogger } from '../../services/redacted-logger'
import { getInsights } from './insights'
import { listAccounts } from './list-accounts'
import { listPosts } from './list-posts'
import {
  createComment,
  deleteComment,
  listComments,
  updateComment,
  type FacebookPageCommentsDeps,
} from './comments'
import {
  cancelScheduled,
  deletePost,
  publish,
  schedule,
  updatePost,
  type FacebookPagePublisherDeps,
  type IdempotencyCache,
} from './publish'
import type { RateLimitCheck } from './rate-limit-check'
import { uploadMedia } from './upload-media'
import { facebookPageWebhookAdapter } from './webhook'

// ============================================================================
// Constants
// ============================================================================

/** Canonical type id / slug. MUST match the seed row in migration 011. */
export const FB_PAGE_TYPE_SLUG = 'facebook-page'

// ============================================================================
// Dependencies
// ============================================================================

export interface FacebookPageMethodsDeps {
  /** Injectable fetch — defaults to the global `fetch`. */
  fetchImpl?: typeof fetch
  /** Override the Graph API base URL — tests inject a stub. */
  graphApiBase?: string
  /**
   * Provide the rate-limiter env at call time. The capability registry
   * doesn't carry the Worker env, so adapters retrieve it from request
   * context via this hook. Returning `undefined` makes the adapter fail-open
   * (skip rate limiter).
   */
  resolveRuntime?: () => unknown
  /**
   * Provide the idempotency cache (KV namespace) at call time. Same reason
   * as `resolveRuntime` — must come from Worker context.
   */
  resolveIdempotencyCache?: () => IdempotencyCache | undefined
  /**
   * Rate-limit check callback. The Worker entrypoint binds this to the
   * connectors rate-limiter service; if omitted the adapter fail-opens
   * (skip rate limiter — safe for tests + dev).
   */
  rateLimitCheck?: RateLimitCheck
  /** Override the default redacted logger — useful in tests. */
  logger?: SocialPostingRedactedLogger
}

// ============================================================================
// buildFacebookPageMethods
// ============================================================================

export function buildFacebookPageMethods(
  deps: FacebookPageMethodsDeps = {},
): AdapterMethods<'social-posting'> {
  const fetchImpl = deps.fetchImpl ?? fetch
  const graphApiBase = deps.graphApiBase

  function makeCallCtx(adapterCtx: AdapterContext): SocialPostingCallCtx {
    const runtime = deps.resolveRuntime?.() ?? {}
    return {
      instanceId: adapterCtx.instanceId,
      companyId: adapterCtx.companyId,
      config: adapterCtx.config,
      runtime,
      // Same `unknown`-typed handle; the rate-limit check callback (deps.
      // rateLimitCheck) narrows at the call site. Adapters never reach
      // into this field directly.
      rateLimiter: runtime,
      redactedLogger:
        deps.logger ??
        createRedactedLogger({ prefix: '[connectors/facebook-page]' }),
    }
  }

  function publisherDeps(): FacebookPagePublisherDeps {
    const out: FacebookPagePublisherDeps = { fetchImpl }
    if (graphApiBase) out.graphApiBase = graphApiBase
    const cache = deps.resolveIdempotencyCache?.()
    if (cache) out.idempotencyCache = cache
    if (deps.rateLimitCheck) out.rateLimitCheck = deps.rateLimitCheck
    return out
  }

  function commentsDeps(): FacebookPageCommentsDeps {
    const out: FacebookPageCommentsDeps = { fetchImpl }
    if (graphApiBase) out.graphApiBase = graphApiBase
    if (deps.rateLimitCheck) out.rateLimitCheck = deps.rateLimitCheck
    return out
  }

  return {
    async publish(args, adapterCtx) {
      const ctx = makeCallCtx(adapterCtx)
      return publish(args, adapterCtx.accessToken, ctx, publisherDeps())
    },
    async schedule(args, adapterCtx) {
      const ctx = makeCallCtx(adapterCtx)
      return schedule(args, adapterCtx.accessToken, ctx, publisherDeps())
    },
    async cancelScheduled(args, adapterCtx) {
      const ctx = makeCallCtx(adapterCtx)
      return cancelScheduled(args, adapterCtx.accessToken, ctx, publisherDeps())
    },
    async deletePost(args, adapterCtx) {
      const ctx = makeCallCtx(adapterCtx)
      return deletePost(args, adapterCtx.accessToken, ctx, publisherDeps())
    },
    async updatePost(args, adapterCtx) {
      const ctx = makeCallCtx(adapterCtx)
      return updatePost(args, adapterCtx.accessToken, ctx, publisherDeps())
    },
    async uploadMedia(args, adapterCtx) {
      const ctx = makeCallCtx(adapterCtx)
      return uploadMedia(args, adapterCtx.accessToken, ctx, {
        fetchImpl,
        ...(graphApiBase ? { graphApiBase } : {}),
        ...(deps.rateLimitCheck ? { rateLimitCheck: deps.rateLimitCheck } : {}),
      })
    },
    async listAccounts(args, adapterCtx) {
      const ctx = makeCallCtx(adapterCtx)
      return listAccounts(args, adapterCtx.accessToken, ctx, {
        fetchImpl,
        ...(graphApiBase ? { graphApiBase } : {}),
      })
    },
    async listPosts(args, adapterCtx) {
      const ctx = makeCallCtx(adapterCtx)
      return listPosts(args, adapterCtx.accessToken, ctx, {
        fetchImpl,
        ...(graphApiBase ? { graphApiBase } : {}),
        ...(deps.rateLimitCheck ? { rateLimitCheck: deps.rateLimitCheck } : {}),
      })
    },
    async getInsights(args, adapterCtx) {
      const ctx = makeCallCtx(adapterCtx)
      return getInsights(args, adapterCtx.accessToken, ctx, {
        fetchImpl,
        ...(graphApiBase ? { graphApiBase } : {}),
        ...(deps.rateLimitCheck ? { rateLimitCheck: deps.rateLimitCheck } : {}),
      })
    },
    async listComments(args, adapterCtx) {
      const ctx = makeCallCtx(adapterCtx)
      return listComments(args, adapterCtx.accessToken, ctx, commentsDeps())
    },
    async createComment(args, adapterCtx) {
      const ctx = makeCallCtx(adapterCtx)
      return createComment(args, adapterCtx.accessToken, ctx, commentsDeps())
    },
    async updateComment(args, adapterCtx) {
      const ctx = makeCallCtx(adapterCtx)
      return updateComment(args, adapterCtx.accessToken, ctx, commentsDeps())
    },
    async deleteComment(args, adapterCtx) {
      const ctx = makeCallCtx(adapterCtx)
      return deleteComment(args, adapterCtx.accessToken, ctx, commentsDeps())
    },
  }
}

// ============================================================================
// registerFacebookPageAdapter — top-level register helper
// ============================================================================

export interface RegisterFacebookPageOpts extends FacebookPageMethodsDeps {}

/**
 * Register the Facebook Page adapter with the capability registry. Idempotent:
 * re-calling overwrites the prior registration (matches the semantics of
 * the underlying registry). Safe to call at module-load + test-reset points.
 */
export function registerFacebookPageAdapter(
  opts: RegisterFacebookPageOpts = {},
): void {
  registerCapability(FB_PAGE_TYPE_SLUG, 'social-posting', buildFacebookPageMethods(opts))
  registerWebhookAdapter(FB_PAGE_TYPE_SLUG, facebookPageWebhookAdapter)
}
